import { useState } from "react";
import { useTranslation } from "react-i18next";
import truncate from "lodash/truncate";

const useTagValidation = (tags, allTagsData) => {
  const { t } = useTranslation("common");
  const [errorMessage, setErrorMessage] = useState("");

  const validateTagInput = (tagName, selectedTagsData = []) => {
    const trimmedName = tagName.trim();

    if (trimmedName.length > 255) {
      setErrorMessage(t("max_length", { fieldName: `"${truncate(trimmedName, { length: 20 })}"`, maxLength: 255 }));
      return false;
    }

    const existsInCurrentTags = tags.some((tag) => tag.name.toLowerCase() === trimmedName.toLowerCase());
    const isDuplicateWithSelected = selectedTagsData.some((selectedTag) => selectedTag.name.toLowerCase() === trimmedName.toLowerCase());

    if (existsInCurrentTags || isDuplicateWithSelected) {
      setErrorMessage(t("field_already_exists", { fieldName: `"${truncate(trimmedName, { length: 20 })}"` }));
      return false;
    }

    return true;
  };

  const getNextDisplayOrder = (resourceTags) => {
    return resourceTags.length > 0 ? Math.max(...resourceTags.map((tag) => tag.display_order || 0)) + 1 : 1;
  };

  const processInputValueTag = (inputValue, selectedTagsData = []) => {
    if (!inputValue.trim()) return null;

    if (!validateTagInput(inputValue, selectedTagsData)) return null;

    return { name: inputValue.trim() };
  };

  const processSelectedTags = (selectedTags) => {
    if (selectedTags.length === 0) return [];

    return selectedTags
      .map((option) => {
        if (option.__isNew__) {
          return { name: option.label };
        }
        return allTagsData?.items?.find((tag) => tag.id === option.value);
      })
      .filter(Boolean);
  };

  const assignDisplayOrders = (tagsArray) => {
    let currentOrder = getNextDisplayOrder(tags);
    return tagsArray.map((tag) => ({
      ...tag,
      display_order: currentOrder++,
    }));
  };

  const prepareTags = (inputValue, selectedTags) => {
    const tagsToAdd = [];

    const selectedTagsData = processSelectedTags(selectedTags);
    const inputTag = processInputValueTag(inputValue, selectedTagsData);

    tagsToAdd.push(...selectedTagsData);

    if (inputTag === null && inputValue.trim()) {
      return null;
    }

    if (inputTag) {
      tagsToAdd.push(inputTag);
    }

    const tagsWithDisplayOrder = assignDisplayOrders(tagsToAdd);

    return tagsWithDisplayOrder;
  };

  const clearError = () => {
    setErrorMessage("");
  };

  return {
    errorMessage,
    setErrorMessage,
    validateTagInput,
    processInputValueTag,
    processSelectedTags,
    prepareTags,
    clearError,
  };
};

export default useTagValidation;
